import { useMemo, useState } from 'react'
import { api, query } from '../api/client'
import { useQuery } from '../api/hooks'
import type { CashLocation, Currency, ExchangeRate } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { P } from '../auth/permissions'
import {
  ErrorBanner,
  Field,
  Loading,
  Modal,
  Panel,
  Tag,
  formatDate,
  formatDateTime,
  isoDate,
} from '../components/ui'

/** Rates carry six decimals in the database; two would round a USD balance visibly off. */
function rateText(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 6 })
}

/**
 * Exchange rates — effective-dated, one rate per currency per date (BUILD_PLAN §3.6).
 *
 * A rate applies from its date until the next one supersedes it. Nothing in a foreign currency can
 * be posted before a rate exists on or before the posting date: the posting engine rejects the
 * entry rather than guess. That makes the "no rate" case on this screen a blocker, not a warning,
 * which is why the currencies actually attached to a bank or safe are called out first.
 */
export function ExchangeRatesPage() {
  const { can } = useAuth()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<Currency | 'new' | null>(null)
  const [selected, setSelected] = useState('')
  const [error, setError] = useState<unknown>()

  const today = isoDate(new Date())

  // Inactive ones included, because this is the screen that maintains them: a currency taken out
  // of use has to be visible here in order to be brought back. Every picker elsewhere calls the
  // same endpoint without the flag and sees only what may still be chosen.
  const currencies = useQuery<Currency[]>(`/currencies${query({ includeInactive: true })}`)

  // Unfiltered, and the source for the "as of today" strip — the filter below must not change
  // which currencies get flagged as unusable.
  const rates = useQuery<ExchangeRate[]>('/exchange-rates')

  const filtered = useQuery<ExchangeRate[]>(
    selected ? `/exchange-rates${query({ currency: selected })}` : null,
    [selected],
  )

  // Only to mark which currencies are actually in use. A user who may see rates but not the books
  // simply does not get the annotation.
  const locations = useQuery<CashLocation[]>(can(P.cashBook.view) ? '/cash-books/locations' : null)

  const baseCode = currencies.data?.find((c) => c.isBase)?.code ?? 'EGP'

  // The rate strip and the picker are about currencies you can still post in. A retired one keeps
  // its history in the table below and does not belong in either.
  const foreign = useMemo(
    () => (currencies.data ?? []).filter((c) => !c.isBase && c.isActive),
    [currencies.data],
  )

  const inUse = useMemo(
    () => new Set((locations.data ?? []).map((l) => l.currency)),
    [locations.data],
  )

  /** The rate in force today: the latest one whose effective date has arrived. */
  const effective = useMemo(() => {
    const map = new Map<string, ExchangeRate>()

    for (const rate of rates.data ?? []) {
      if (rate.validFrom > today) continue

      const current = map.get(rate.currencyCode)
      if (!current || rate.validFrom > current.validFrom) {
        map.set(rate.currencyCode, rate)
      }
    }

    return map
  }, [rates.data, today])

  const missing = foreign.filter((c) => inUse.has(c.code) && !effective.has(c.code))

  /** Newest first within each currency, each row carrying the rate it replaced. */
  const history = useMemo(() => {
    const rows = (selected ? filtered.data : rates.data) ?? []

    const byCurrency = new Map<string, ExchangeRate[]>()
    for (const rate of rows) {
      const list = byCurrency.get(rate.currencyCode)
      if (list) list.push(rate)
      else byCurrency.set(rate.currencyCode, [rate])
    }

    const out: Array<ExchangeRate & { previous?: number }> = []

    for (const code of [...byCurrency.keys()].sort()) {
      const ascending = [...byCurrency.get(code)!].sort((a, b) =>
        a.validFrom.localeCompare(b.validFrom),
      )

      const withPrevious = ascending.map((rate, index) => ({
        ...rate,
        previous: index > 0 ? ascending[index - 1].rateToBase : undefined,
      }))

      out.push(...withPrevious.reverse())
    }

    return out
  }, [selected, filtered.data, rates.data])

  const loading = rates.loading || filtered.loading

  /**
   * Retires a currency or brings it back. Refused by the server while a bank or safe still uses
   * it — an inactive currency has no rate to find, so a book denominated in one could not post.
   */
  async function setActive(currency: Currency, active: boolean) {
    setError(undefined)
    try {
      await api(`/currencies/${currency.code}/${active ? 'reactivate' : 'deactivate'}`, {
        method: 'POST',
      })
      currencies.reload()
    } catch (caught) {
      setError(caught)
    }
  }

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>Currency</label>
          <select value={selected} onChange={(e) => setSelected(e.target.value)}>
            <option value="">All currencies</option>
            {foreign.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} — {c.nameEn}
              </option>
            ))}
          </select>
        </div>

        <div className="spacer" />

        {can(P.currencies.manage) && (
          <>
            <button onClick={() => setEditing('new')}>+ New currency</button>
            <button className="primary" onClick={() => setAdding(true)}>
              + New rate
            </button>
          </>
        )}
      </div>

      <ErrorBanner error={currencies.error} />
      <ErrorBanner error={rates.error ?? filtered.error} />
      <ErrorBanner error={error} onDismiss={() => setError(undefined)} />

      {missing.length > 0 && (
        <div className="banner error">
          <div>
            <strong>
              {missing.map((c) => c.code).join(', ')} {missing.length === 1 ? 'has' : 'have'} a bank
              or safe but no exchange rate.
            </strong>{' '}
            Nothing can be entered in {missing.length === 1 ? 'that book' : 'those books'} until a
            rate exists — the posting engine refuses the entry rather than assume one. Add a rate
            effective on or before the earliest date you intend to post.
          </div>
        </div>
      )}

      {foreign.length > 0 && (
        <div className="grid cols-4" style={{ marginBottom: 16 }}>
          {foreign.map((currency) => {
            const rate = effective.get(currency.code)
            const used = inUse.has(currency.code)

            return (
              <div key={currency.code} className={!rate && used ? 'metric alert' : 'metric'}>
                <div className="label">
                  1 {currency.code} in {baseCode}
                </div>
                <div className="value">{rate ? rateText(rate.rateToBase) : '—'}</div>
                <div className="hint">
                  {rate ? (
                    <>
                      In force since {formatDate(rate.validFrom)}
                      {rate.source ? ` · ${rate.source}` : ''}
                    </>
                  ) : used ? (
                    'No rate. This currency has a book and cannot be posted to.'
                  ) : (
                    'No rate recorded, and no bank or safe uses it.'
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {loading && <Loading what="Loading rates" />}

      {/* The currencies themselves. Read-only until now: the seeder created EGP and USD and there
          was no way to add a third, so an office banking in EUR had a screen full of rates it
          could not attach to anything. */}
      <Panel flush title="Currencies">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th style={{ width: 90 }}>Code</th>
                <th>Name</th>
                <th style={{ width: 90 }}>Symbol</th>
                <th style={{ width: 90 }} className="num">
                  Decimals
                </th>
                <th style={{ width: 140 }}>Status</th>
                <th style={{ width: 170 }} />
              </tr>
            </thead>
            <tbody>
              {(currencies.data ?? []).map((currency) => (
                <tr key={currency.code} className={currency.isActive ? undefined : 'muted'}>
                  <td className="strong">{currency.code}</td>
                  <td>
                    {currency.nameEn}
                    {currency.nameAr && <span className="soft"> · {currency.nameAr}</span>}
                  </td>
                  <td>{currency.symbol ?? '—'}</td>
                  <td className="num">{currency.decimalPlaces}</td>
                  <td>
                    {currency.isBase ? (
                      <Tag kind="info">Base</Tag>
                    ) : currency.isActive ? (
                      <Tag kind="ok">In use</Tag>
                    ) : (
                      <Tag kind="warn">Retired</Tag>
                    )}
                  </td>
                  <td>
                    {can(P.currencies.manage) && (
                      <>
                        <button className="subtle small" onClick={() => setEditing(currency)}>
                          Edit
                        </button>

                        {/* The base currency has no toggle at all. Every base amount in the ledger
                            is stated in it, so retiring it is a migration rather than a click —
                            the server refuses it either way. */}
                        {!currency.isBase && (
                          <button
                            className="subtle small"
                            onClick={() => void setActive(currency, !currency.isActive)}
                          >
                            {currency.isActive ? 'Retire' : 'Reactivate'}
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel
        flush
        title={selected ? `${selected} rate history` : 'Rate history'}
        actions={
          <span className="muted" style={{ fontSize: 12 }}>
            Newest first · most recent 200
          </span>
        }
      >
        {history.length === 0 && !loading ? (
          <div className="empty">
            No rates recorded{selected ? ` for ${selected}` : ''} yet.
          </div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  {!selected && <th style={{ width: 90 }}>Currency</th>}
                  <th style={{ width: 130 }}>Effective from</th>
                  <th style={{ width: 140 }} className="num">
                    Rate to {baseCode}
                  </th>
                  <th style={{ width: 130 }} className="num">
                    Change
                  </th>
                  <th style={{ width: 200 }}>Source</th>
                  <th>Recorded</th>
                </tr>
              </thead>
              <tbody>
                {history.map((rate) => {
                  const movement =
                    rate.previous !== undefined
                      ? (rate.rateToBase - rate.previous) / rate.previous
                      : undefined

                  return (
                    <tr key={rate.id}>
                      {!selected && <td className="strong">{rate.currencyCode}</td>}
                      <td>
                        {formatDate(rate.validFrom)}{' '}
                        {rate.validFrom > today && <Tag kind="info">not yet in effect</Tag>}
                      </td>
                      <td className="num strong">{rateText(rate.rateToBase)}</td>
                      <td className={movement && movement < 0 ? 'num negative' : 'num'}>
                        {movement === undefined
                          ? ''
                          : `${movement > 0 ? '+' : ''}${(movement * 100).toFixed(2)}%`}
                      </td>
                      <td className="soft">{rate.source ?? ''}</td>
                      <td className="muted" style={{ fontSize: 12 }}>
                        {formatDateTime(rate.modifiedAt ?? rate.createdAt)}
                        {rate.modifiedBy || rate.createdBy
                          ? ` · ${rate.modifiedBy ?? rate.createdBy}`
                          : ''}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <div className="muted" style={{ fontSize: 12 }}>
        A rate holds from its effective date until the next one replaces it. Entries already posted
        keep the base amounts they were posted with, so adding a back-dated rate never restates
        them — and any single entry may still override the rate on the line, which is the normal
        case when the bank gives a rate other than the official one.
      </div>

      {adding && (
        <NewRate
          baseCode={baseCode}
          currencies={foreign}
          existing={rates.data ?? []}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false)
            rates.reload()
            if (selected) filtered.reload()
          }}
        />
      )}

      {editing && (
        <CurrencyForm
          currency={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            currencies.reload()
          }}
        />
      )}
    </>
  )
}

/**
 * Adding or renaming a currency.
 *
 * <p>The code is fixed once saved: it is the foreign key on every rate, cash location and journal
 * line, so editing it here would leave those pointing at a currency that no longer exists. A
 * mistyped code is retired and replaced rather than renamed.</p>
 */
function CurrencyForm({
  currency,
  onClose,
  onSaved,
}: {
  currency: Currency | null
  onClose: () => void
  onSaved: () => void
}) {
  const [form, setForm] = useState({
    code: currency?.code ?? '',
    nameEn: currency?.nameEn ?? '',
    nameAr: currency?.nameAr ?? '',
    symbol: currency?.symbol ?? '',
    decimalPlaces: String(currency?.decimalPlaces ?? 2),
  })

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((previous) => ({ ...previous, [key]: value }))
  }

  const code = form.code.trim().toUpperCase()
  const places = Number(form.decimalPlaces)

  // The same three rules the server enforces, so the form says so before the round trip rather
  // than after it. The server still checks — this is a courtesy, not the control.
  const valid =
    /^[A-Z]{3}$/.test(code) &&
    form.nameEn.trim() !== '' &&
    Number.isInteger(places) &&
    places >= 0 &&
    places <= 4

  async function save() {
    setBusy(true)
    setError(undefined)

    try {
      await api(currency ? `/currencies/${currency.code}` : '/currencies', {
        method: currency ? 'PUT' : 'POST',
        body: {
          code,
          nameEn: form.nameEn.trim(),
          nameAr: form.nameAr.trim(),
          symbol: form.symbol.trim() || null,
          decimalPlaces: places,
        },
      })
      onSaved()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title={currency ? `${currency.code} — currency` : 'New currency'}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={busy || !valid} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} onDismiss={() => setError(undefined)} />

      <div className="grid cols-2">
        <Field label="Code" hint="ISO 4217 — three letters, e.g. EUR">
          <input
            value={form.code}
            disabled={currency !== null}
            maxLength={3}
            placeholder="EUR"
            onChange={(e) => set('code', e.target.value.toUpperCase())}
          />
        </Field>
        <Field label="Symbol" hint="Optional, for display only">
          <input value={form.symbol ?? ''} onChange={(e) => set('symbol', e.target.value)} />
        </Field>
        <Field label="Name (English)">
          <input value={form.nameEn} onChange={(e) => set('nameEn', e.target.value)} />
        </Field>
        <Field label="Name (Arabic)">
          <input dir="rtl" value={form.nameAr} onChange={(e) => set('nameAr', e.target.value)} />
        </Field>
        <Field label="Decimal places" hint="0–4. Amounts are stored to four.">
          <input
            type="number"
            min={0}
            max={4}
            value={form.decimalPlaces}
            onChange={(e) => set('decimalPlaces', e.target.value)}
          />
        </Field>
      </div>

      {!currency && (
        <p className="muted" style={{ fontSize: 12 }}>
          A new currency is never the base one. Every base amount in the ledger is denominated in
          the base currency, so changing which one that is would restate history rather than record
          it.
        </p>
      )}
    </Modal>
  )
}

function NewRate({
  baseCode,
  currencies,
  existing,
  onClose,
  onSaved,
}: {
  baseCode: string
  currencies: Currency[]
  existing: ExchangeRate[]
  onClose: () => void
  onSaved: () => void
}) {
  const [form, setForm] = useState({
    currencyCode: currencies[0]?.code ?? '',
    validFrom: isoDate(new Date()),
    rateToBase: '',
    source: '',
  })

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((previous) => ({ ...previous, [key]: value }))
  }

  const parsed = Number(form.rateToBase)
  const valid = form.currencyCode !== '' && Number.isFinite(parsed) && parsed > 0

  // The endpoint upserts on (currency, effective date) — there is a unique index behind it. Say so
  // before the save rather than let a rate quietly change.
  const replacing = existing.find(
    (rate) => rate.currencyCode === form.currencyCode && rate.validFrom === form.validFrom,
  )

  async function save() {
    setBusy(true)
    setError(undefined)

    try {
      await api('/exchange-rates', {
        method: 'POST',
        body: {
          currencyCode: form.currencyCode,
          validFrom: form.validFrom,
          rateToBase: parsed,
          source: form.source.trim() || null,
        },
      })

      onSaved()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title="New exchange rate"
      width="narrow"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={busy || !valid} onClick={() => void save()}>
            {busy ? 'Saving…' : replacing ? 'Replace' : 'Add rate'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} onDismiss={() => setError(undefined)} />

      <div className="grid cols-2">
        <Field label="Currency">
          <select
            value={form.currencyCode}
            onChange={(e) => set('currencyCode', e.target.value)}
          >
            {currencies.map((currency) => (
              <option key={currency.code} value={currency.code}>
                {currency.code} — {currency.nameEn}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Effective from" hint="Applies to every entry from this date onwards.">
          <input
            type="date"
            value={form.validFrom}
            onChange={(e) => set('validFrom', e.target.value)}
          />
        </Field>
      </div>

      <Field
        label={`Rate to ${baseCode}`}
        hint={`How many ${baseCode} one ${form.currencyCode || 'unit'} buys.`}
      >
        <input
          className="num"
          inputMode="decimal"
          placeholder="48.5000"
          value={form.rateToBase}
          onChange={(e) => set('rateToBase', e.target.value)}
        />
      </Field>

      <Field label="Source" hint="Where it came from — CBE, the bank's advice, the contract.">
        <input
          value={form.source}
          onChange={(e) => set('source', e.target.value)}
          placeholder="CBE"
        />
      </Field>

      {valid && (
        <div className="banner info" style={{ marginBottom: 0 }}>
          <div>
            1 {form.currencyCode} = {rateText(parsed)} {baseCode} · 1,000 {form.currencyCode} ={' '}
            {(parsed * 1000).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{' '}
            {baseCode}
          </div>
        </div>
      )}

      {replacing && (
        <div className="banner warn" style={{ marginBottom: 0 }}>
          <div>
            {form.currencyCode} already has a rate of{' '}
            <strong>{rateText(replacing.rateToBase)}</strong> effective{' '}
            {formatDate(replacing.validFrom)}. Saving replaces it.
          </div>
        </div>
      )}
    </Modal>
  )
}
